import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, Platform } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';
import { PrimaryButton } from './PrimaryButton';

/**
 * Post-auth confirmation with a pop-in checkmark, then the explicit
 * "Ouvrir Paprika" CTA (the screen suppresses its auto-redirect so this shows).
 */
export const SuccessPanel: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { t } = useTranslation();
  const pop = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.spring(pop, {
      toValue: 1,
      tension: 140,
      friction: 8,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [pop]);

  return (
    <View style={styles.wrap}>
      <Animated.View
        style={[
          styles.badge,
          {
            opacity: pop,
            transform: [{ scale: pop.interpolate({ inputRange: [0, 1], outputRange: [0.4, 1] }) }],
          },
        ]}
      >
        <Feather name="check" size={38} color={Paprika.accent} />
      </Animated.View>

      <View style={styles.textBlock}>
        <Text style={styles.title}>{t('auth.successTitle')}</Text>
        <Text style={styles.sub}>{t('auth.successSub')}</Text>
      </View>

      <View style={styles.cta}>
        <PrimaryButton label={t('auth.openPaprika')} onPress={onOpen} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    alignItems: 'center',
    gap: 18,
    paddingTop: 8,
  },
  badge: {
    width: 76,
    height: 76,
    borderRadius: 38,
    backgroundColor: Paprika.brandSoft,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: { boxShadow: 'inset 0 0 0 1.5px #f0c5b1' },
      default: {},
    }),
  },
  textBlock: {
    alignItems: 'center',
  },
  title: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 26,
    color: Paprika.textPrimary,
  },
  sub: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textMuted,
    textAlign: 'center',
    marginTop: 8,
    maxWidth: 300,
  },
  cta: {
    width: '100%',
    marginTop: 6,
  },
});
